import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Modal,
  Button,
  Space,
  Tag,
  Popover,
  Popconfirm,
  Spin,
  Alert,
  Tooltip,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  EnvironmentOutlined,
  UserOutlined,
  SyncOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

const CATEGORY_STYLES = {
  MDD: { bg: "#fff0f6", border: "#eb2f96", text: "#c41d7f" },
  Sports: { bg: "#f6ffed", border: "#52c41a", text: "#389e0d" },
  Languages: { bg: "#e6f4ff", border: "#1677ff", text: "#0958d9" },
  "Academic Clubs": { bg: "#f9f0ff", border: "#722ed1", text: "#531dab" },
  "Special Interest": { bg: "#fff7e6", border: "#fa8c16", text: "#d46b08" },
};

const STATUS_COLORS = {
  Draft: "default",
  "Pending Head Teacher Approval": "orange",
  "Pending Deputy Principal Approval": "blue",
  "Pending Principal Approval": "purple",
  Published: "green",
};

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function getMonthGrid(year, month) {
  const firstDay = dayjs(new Date(year, month, 1));
  const lastDay = firstDay.endOf("month");
  const startDay = firstDay.startOf("isoWeek");
  const endDay = lastDay.endOf("isoWeek");
  const days = [];
  let cur = startDay;
  while (!cur.isAfter(endDay, "day")) {
    days.push(cur);
    cur = cur.add(1, "day");
  }
  return days;
}

function getWeekDays(referenceDate) {
  const start = referenceDate.startOf("isoWeek");
  return Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
}

function buildEventsMap(events) {
  const map = {};
  for (const event of events) {
    if (!event.scheduledDate) continue;
    const start = dayjs(event.scheduledDate);
    const end = event.endDate ? dayjs(event.endDate) : start;
    let cur = start;
    while (!cur.isAfter(end, "day")) {
      const key = cur.format("YYYY-MM-DD");
      if (!map[key]) map[key] = [];
      if (!map[key].find((e) => e.id === event.id)) {
        map[key].push(event);
      }
      cur = cur.add(1, "day");
    }
  }
  return map;
}

const formatTeacherNames = (names) => {
  if (Array.isArray(names)) return names.filter(Boolean).join(", ");
  return names || "";
};

const EventDetail = ({
  event,
  canEdit,
  onEdit,
  onCancel,
  onDelete,
  onClose,
}) => {
  const style = CATEGORY_STYLES[event.category] || {
    bg: "#f5f5f5",
    border: "#d9d9d9",
    text: "#555",
  };
  const responsibleTeachers = formatTeacherNames(event.responsibleTeachersName);
  return (
    <div style={{ width: 300 }}>
      <div
        style={{
          backgroundColor: style.bg,
          borderTop: `4px solid ${style.border}`,
          padding: "12px 16px",
          marginBottom: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Space size={4} wrap>
            <Tag color={style.border} style={{ marginRight: 0 }}>
              {event.category}
            </Tag>
            {event.eventLevel && <Tag>{event.eventLevel}</Tag>}
            {event.isCancelled && <Tag color="error">Cancelled</Tag>}
          </Space>
          <Button
            type="text"
            size="small"
            onClick={onClose}
            style={{
              padding: "0 4px",
              height: 20,
              lineHeight: "20px",
              fontSize: 12,
            }}
          >
            ✕
          </Button>
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "#1a1a1a",
            marginTop: 8,
            textDecoration: event.isCancelled ? "line-through" : "none",
          }}
        >
          {event.title}
        </div>
      </div>

      <div style={{ padding: "12px 16px" }}>
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <CalendarOutlined
              style={{ color: "#888", marginTop: 3, flexShrink: 0 }}
            />
            <div>
              <div>
                {dayjs(event.scheduledDate).format("dddd, MMMM D, YYYY")}
              </div>
              {event.endDate && event.endDate !== event.scheduledDate && (
                <div style={{ color: "#888", fontSize: 12 }}>
                  until {dayjs(event.endDate).format("MMM D, YYYY")}
                </div>
              )}
            </div>
          </div>
          {event.venue && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <EnvironmentOutlined
                style={{ color: "#888", marginTop: 3, flexShrink: 0 }}
              />
              <span>{event.venue}</span>
            </div>
          )}
          {responsibleTeachers && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <UserOutlined
                style={{ color: "#888", marginTop: 3, flexShrink: 0 }}
              />
              <span>{responsibleTeachers}</span>
            </div>
          )}
          {event.description && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <InfoCircleOutlined
                style={{ color: "#888", marginTop: 3, flexShrink: 0 }}
              />
              <span style={{ color: "#555", fontSize: 12 }}>
                {event.description}
              </span>
            </div>
          )}
          {event.cancellationReason && (
            <Alert
              message={event.cancellationReason}
              type="error"
              showIcon
              style={{ fontSize: 12, padding: "4px 8px" }}
            />
          )}
        </Space>

        {canEdit && !event.isCancelled && (
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid #f0f0f0",
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                onClose();
                onEdit(event);
              }}
            >
              Edit
            </Button>
            <Popconfirm
              title="Cancel this event?"
              onConfirm={() => {
                onClose();
                onCancel(event);
              }}
              okText="Cancel Event"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" icon={<StopOutlined />} danger>
                Cancel
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Delete this event?"
              description="This cannot be undone."
              onConfirm={() => {
                onClose();
                onDelete(event.id);
              }}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" icon={<DeleteOutlined />} type="text" danger>
                Delete
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>
    </div>
  );
};

const EventChip = ({ event, canEdit, onEdit, onCancel, onDelete }) => {
  const [open, setOpen] = useState(false);
  const style = CATEGORY_STYLES[event.category] || {
    bg: "#f5f5f5",
    border: "#d9d9d9",
    text: "#555",
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="right"
      arrow={false}
      overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: "hidden" }}
      content={
        <EventDetail
          event={event}
          canEdit={canEdit}
          onEdit={onEdit}
          onCancel={onCancel}
          onDelete={onDelete}
          onClose={() => setOpen(false)}
        />
      }
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: style.bg,
          borderLeft: `3px solid ${style.border}`,
          color: style.text,
          fontSize: 11,
          padding: "1px 5px",
          borderRadius: "0 3px 3px 0",
          marginBottom: 2,
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textDecoration: event.isCancelled ? "line-through" : "none",
          opacity: event.isCancelled ? 0.55 : 1,
          userSelect: "none",
        }}
        title={event.title}
      >
        {event.title}
      </div>
    </Popover>
  );
};

const MonthView = ({
  year,
  month,
  eventsMap,
  today,
  canEdit,
  termStart,
  termEnd,
  onDayClick,
  onEditEvent,
  onCancelEvent,
  onDeleteEvent,
}) => {
  const days = useMemo(() => getMonthGrid(year, month), [year, month]);
  const weeks = useMemo(() => {
    const result = [];
    for (let i = 0; i < days.length; i += 7) result.push(days.slice(i, i + 7));
    return result;
  }, [days]);

  return (
    <div
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "#fff",
        minWidth: 560,
      }}
    >
      {/* Day headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          backgroundColor: "#fafafa",
          borderBottom: "1px solid #e8e8e8",
        }}
      >
        {DAY_NAMES.map((day, i) => (
          <div
            key={day}
            style={{
              textAlign: "center",
              padding: "8px 4px",
              fontSize: 12,
              fontWeight: 600,
              color: i >= 5 ? "#cf1322" : "#595959",
              borderRight: i < 6 ? "1px solid #e8e8e8" : "none",
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Weeks */}
      <div>
        {weeks.map((week, wi) => (
          <div
            key={wi}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              borderBottom:
                wi < weeks.length - 1 ? "1px solid #e8e8e8" : "none",
              minHeight: 100,
            }}
          >
            {week.map((day, di) => {
              const key = day.format("YYYY-MM-DD");
              const dayEvents = eventsMap[key] || [];
              const isToday = day.isSame(today, "day");
              const isCurrentMonth = day.month() === month;
              const isWeekend = di >= 5;
              const isOutOfRange =
                (termStart && day.isBefore(dayjs(termStart), "day")) ||
                (termEnd && day.isAfter(dayjs(termEnd), "day"));
              const isClickable = canEdit && !isOutOfRange;
              const baseBg = isOutOfRange
                ? "#f0f0f0"
                : isCurrentMonth
                  ? "#fff"
                  : "#fafafa";

              return (
                <div
                  key={di}
                  onClick={() => isClickable && onDayClick(day)}
                  style={{
                    borderRight: di < 6 ? "1px solid #e8e8e8" : "none",
                    padding: "4px 6px",
                    backgroundColor: baseBg,
                    cursor: isClickable ? "pointer" : "default",
                    position: "relative",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (isClickable)
                      e.currentTarget.style.backgroundColor = "#f0f5ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = baseBg;
                  }}
                >
                  <div style={{ marginBottom: 3 }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        fontSize: 12,
                        fontWeight: isToday ? 700 : 400,
                        backgroundColor: isToday ? "#1677ff" : "transparent",
                        color: isToday
                          ? "#fff"
                          : isOutOfRange
                            ? "#bfbfbf"
                            : isCurrentMonth
                              ? isWeekend
                                ? "#cf1322"
                                : "#262626"
                              : "#bfbfbf",
                      }}
                    >
                      {day.date()}
                    </span>
                  </div>
                  {dayEvents.slice(0, 3).map((event) => (
                    <EventChip
                      key={event.id}
                      event={event}
                      canEdit={canEdit}
                      onEdit={onEditEvent}
                      onCancel={onCancelEvent}
                      onDelete={onDeleteEvent}
                    />
                  ))}
                  {dayEvents.length > 3 && (
                    <div
                      style={{ fontSize: 10, color: "#1677ff", paddingLeft: 2 }}
                    >
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const WeekView = ({
  referenceDate,
  eventsMap,
  today,
  canEdit,
  termStart,
  termEnd,
  onDayClick,
  onEditEvent,
  onCancelEvent,
  onDeleteEvent,
}) => {
  const days = useMemo(() => getWeekDays(referenceDate), [referenceDate]);

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "#fff",
        minWidth: 560,
      }}
    >
      {/* Day headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          backgroundColor: "#fafafa",
          borderBottom: "1px solid #e8e8e8",
        }}
      >
        {days.map((day, i) => {
          const isToday = day.isSame(today, "day");
          const isWeekend = i >= 5;
          return (
            <div
              key={i}
              style={{
                textAlign: "center",
                padding: "8px 4px",
                borderRight: i < 6 ? "1px solid #e8e8e8" : "none",
                backgroundColor: isToday ? "#e6f4ff" : "transparent",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: isWeekend ? "#cf1322" : "#595959",
                  marginBottom: 4,
                }}
              >
                {DAY_NAMES[i]}
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  fontSize: 16,
                  fontWeight: isToday ? 700 : 400,
                  backgroundColor: isToday ? "#1677ff" : "transparent",
                  color: isToday ? "#fff" : isWeekend ? "#cf1322" : "#262626",
                }}
              >
                {day.date()}
              </span>
            </div>
          );
        })}
      </div>

      {/* Event columns */}
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          alignContent: "start",
          minHeight: 300,
        }}
      >
        {days.map((day, i) => {
          const key = day.format("YYYY-MM-DD");
          const dayEvents = eventsMap[key] || [];
          const isWeekend = i >= 5;
          const isOutOfRange =
            (termStart && day.isBefore(dayjs(termStart), "day")) ||
            (termEnd && day.isAfter(dayjs(termEnd), "day"));
          const isClickable = canEdit && !isOutOfRange;
          const baseBg = isOutOfRange
            ? "#f0f0f0"
            : isWeekend
              ? "#fffbf9"
              : "#fff";

          return (
            <div
              key={i}
              onClick={() => isClickable && onDayClick(day)}
              style={{
                borderRight: i < 6 ? "1px solid #e8e8e8" : "none",
                padding: "8px 6px",
                minHeight: 300,
                cursor: isClickable ? "pointer" : "default",
                backgroundColor: baseBg,
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) => {
                if (isClickable)
                  e.currentTarget.style.backgroundColor = "#f0f5ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = baseBg;
              }}
            >
              {dayEvents.length === 0 && isClickable && (
                <div
                  style={{
                    color: "#e0e0e0",
                    fontSize: 11,
                    textAlign: "center",
                    paddingTop: 20,
                  }}
                >
                  <PlusOutlined style={{ fontSize: 16 }} />
                </div>
              )}
              {dayEvents.map((event) => (
                <EventChip
                  key={event.id}
                  event={event}
                  canEdit={canEdit}
                  onEdit={onEditEvent}
                  onCancel={onCancelEvent}
                  onDelete={onDeleteEvent}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CurricularCalendarView = ({
  visible,
  onClose,
  calendar,
  events = [],
  loading,
  isAdmin,
  termStart,
  termEnd,
  onAddEvent,
  onEditEvent,
  onCancelEvent,
  onDeleteEvent,
  onSubmit,
  onApprove,
  onReject,
  onRefresh,
}) => {
  const today = useMemo(() => dayjs(), []);
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(today);

  useEffect(() => {
    if (visible) setCurrentDate(termStart ? dayjs(termStart) : today);
  }, [visible, termStart, today]);

  const eventsMap = useMemo(() => buildEventsMap(events), [events]);

  const goToToday = useCallback(() => setCurrentDate(today), [today]);
  const goPrev = useCallback(() => {
    setCurrentDate((d) =>
      view === "month" ? d.subtract(1, "month") : d.subtract(7, "day"),
    );
  }, [view]);
  const goNext = useCallback(() => {
    setCurrentDate((d) =>
      view === "month" ? d.add(1, "month") : d.add(7, "day"),
    );
  }, [view]);

  const navLabel = useMemo(() => {
    if (view === "month") return currentDate.format("MMMM YYYY");
    const ws = currentDate.startOf("isoWeek");
    const we = ws.add(6, "day");
    if (ws.month() === we.month())
      return `${ws.format("MMMM D")} – ${we.format("D, YYYY")}`;
    return `${ws.format("MMM D")} – ${we.format("MMM D, YYYY")}`;
  }, [view, currentDate]);

  if (!calendar) return null;

  const canEdit = isAdmin && calendar.status === "Draft";

  function handlePrint() {
    const tStart = termStart ? dayjs(termStart) : null;
    const tEnd = termEnd ? dayjs(termEnd) : null;

    const firstMonth = tStart
      ? tStart.startOf("month")
      : dayjs().startOf("month");
    const lastMonth = tEnd ? tEnd.startOf("month") : firstMonth;
    const months = [];
    let cur = firstMonth;
    while (!cur.isAfter(lastMonth, "month")) {
      months.push({ year: cur.year(), month: cur.month() });
      cur = cur.add(1, "month");
    }

    // rebuild events map for the print window (no React state)
    const eMap = {};
    for (const ev of events) {
      if (!ev.scheduledDate) continue;
      const s = dayjs(ev.scheduledDate);
      const e = ev.endDate ? dayjs(ev.endDate) : s;
      let d = s;
      while (!d.isAfter(e, "day")) {
        const k = d.format("YYYY-MM-DD");
        if (!eMap[k]) eMap[k] = [];
        if (!eMap[k].find((x) => x.id === ev.id)) eMap[k].push(ev);
        d = d.add(1, "day");
      }
    }

    const CAT = {
      MDD: { bg: "#fff0f6", border: "#eb2f96", text: "#c41d7f" },
      Sports: { bg: "#f6ffed", border: "#52c41a", text: "#389e0d" },
      Languages: { bg: "#e6f4ff", border: "#1677ff", text: "#0958d9" },
      "Academic Clubs": { bg: "#f9f0ff", border: "#722ed1", text: "#531dab" },
      "Special Interest": { bg: "#fff7e6", border: "#fa8c16", text: "#d46b08" },
    };
    const todayStr = dayjs().format("YYYY-MM-DD");
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    function renderMonth(year, month) {
      const firstDay = dayjs(new Date(year, month, 1));
      const startDay = firstDay.startOf("isoWeek");
      const endDay = firstDay.endOf("month").endOf("isoWeek");
      const days = [];
      let c = startDay;
      while (!c.isAfter(endDay, "day")) {
        days.push(c);
        c = c.add(1, "day");
      }
      const weeks = [];
      for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

      let html = `<div class="month-block">
        <div class="month-title">${firstDay.format("MMMM YYYY")}</div>
        <table><thead><tr>`;
      dayNames.forEach((n, i) => {
        html += `<th class="${i >= 5 ? "weekend" : ""}">${n}</th>`;
      });
      html += "</tr></thead><tbody>";

      weeks.forEach((week) => {
        html += "<tr>";
        week.forEach((day, di) => {
          const key = day.format("YYYY-MM-DD");
          const dayEvents = eMap[key] || [];
          const isToday = key === todayStr;
          const isCurrent = day.month() === month;
          const isWeekend = di >= 5;
          const outOfRange =
            (tStart && day.isBefore(tStart, "day")) ||
            (tEnd && day.isAfter(tEnd, "day"));

          const tdClass = outOfRange
            ? "out-of-range"
            : !isCurrent
              ? "other-month"
              : "";
          const numClass = [
            "day-num",
            isToday
              ? "today"
              : outOfRange || !isCurrent
                ? "grey"
                : isWeekend
                  ? "weekend"
                  : "",
          ]
            .filter(Boolean)
            .join(" ");

          html += `<td class="${tdClass}"><span class="${numClass}">${day.date()}</span>`;
          dayEvents.forEach((ev) => {
            const s = CAT[ev.category] || {
              bg: "#f5f5f5",
              border: "#d9d9d9",
              text: "#555",
            };
            html += `<div class="event-chip${ev.isCancelled ? " cancelled" : ""}" style="background:${s.bg};border-left-color:${s.border};color:${s.text}" title="${ev.title.replace(/"/g, "&quot;")}">${ev.title}</div>`;
          });
          html += "</td>";
        });
        html += "</tr>";
      });
      html += "</tbody></table></div>";
      return html;
    }

    const legendHtml = Object.entries(CAT)
      .map(
        ([cat, s]) =>
          `<div class="legend-item"><div class="legend-dot" style="background:${s.bg};border-color:${s.border}"></div><span>${cat}</span></div>`,
      )
      .join("");

    const termLabel = `${calendar.termName} ${calendar.academicYear}`;
    const termRange = [
      tStart ? tStart.format("D MMM") : "",
      tEnd ? tEnd.format("D MMM YYYY") : "",
    ]
      .filter(Boolean)
      .join(" – ");
    const activeCount = events.filter((e) => !e.isCancelled).length;
    const cancelCount = events.filter((e) => e.isCancelled).length;

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Co-Curricular Calendar — ${termLabel}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,Helvetica,sans-serif;padding:24px;color:#1a1a1a;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.header{text-align:center;margin-bottom:18px;padding-bottom:14px;border-bottom:3px solid #1677ff}
.header h1{font-size:20px;color:#1677ff;font-weight:700}
.header p{font-size:12px;color:#555;margin-top:5px}
.stats{display:flex;justify-content:center;gap:32px;margin-bottom:16px;font-size:12px}
.stat{text-align:center}.stat strong{display:block;font-size:18px;color:#1677ff}
.legend{display:flex;gap:14px;flex-wrap:wrap;margin-bottom:18px;font-size:11px;align-items:center}
.legend-item{display:flex;align-items:center;gap:5px}
.legend-dot{width:10px;height:10px;border-radius:2px;border:2px solid;flex-shrink:0}
.month-block{margin-bottom:28px}
.month-title{font-size:14px;font-weight:700;margin-bottom:7px;padding-left:2px}
table{width:100%;border-collapse:collapse;table-layout:fixed}
thead th{background:#e6f0ff;text-align:center;font-size:11px;font-weight:700;padding:6px 4px;border:1px solid #b0bfd0;color:#333}
thead th.weekend{color:#cf1322}
td{border:1px solid #d0d0d0;vertical-align:top;padding:3px 4px;height:75px}
td.out-of-range{background:#f3f3f3}td.other-month{background:#fafafa}
.day-num{font-size:11px;font-weight:500;margin-bottom:2px;display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%}
.day-num.today{background:#1677ff;color:#fff;font-weight:700}
.day-num.grey{color:#c0c0c0}.day-num.weekend{color:#cf1322}
.event-chip{font-size:10px;padding:1px 5px;border-radius:2px;margin-bottom:2px;border-left:3px solid;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:block}
.event-chip.cancelled{text-decoration:line-through;opacity:.55}
.footer{text-align:center;font-size:10px;color:#888;margin-top:20px;border-top:1px solid #d0d0d0;padding-top:12px}
@media print{body{padding:10px}.month-block{page-break-inside:avoid}}
</style>
</head>
<body>
<div class="header">
  <h1>Co-Curricular Calendar</h1>
  <p>${termLabel}${termRange ? " &nbsp;|&nbsp; " + termRange : ""}</p>
</div>
<div class="stats">
  <div class="stat"><strong>${activeCount}</strong>Active Events</div>
  <div class="stat"><strong>${cancelCount}</strong>Cancelled</div>
  <div class="stat"><strong>${months.length}</strong>Month${months.length !== 1 ? "s" : ""}</div>
</div>
<div class="legend">${legendHtml}</div>
${months.map(({ year, month }) => renderMonth(year, month)).join("")}
<div class="footer">Co-Curricular Calendar &nbsp;·&nbsp; Generated ${dayjs().format("DD MMMM YYYY")} &nbsp;·&nbsp; Status: ${calendar.status}</div>
</body>
</html>`;

    const win = window.open("", "_blank", "width=1050,height=820");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 350);
  }
  const activeEvents = events.filter((e) => !e.isCancelled).length;
  const cancelledEvents = events.filter((e) => e.isCancelled).length;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width="96vw"
      style={{ top: 12, padding: 0, marginBottom: 0 }}
      wrapClassName="cocurricular-calendar-modal-wrap"
      styles={{
        body: {
          padding: 0,
          height: "calc(100vh - 80px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      footer={null}
      title={null}
      closable={false}
      destroyOnClose
    >
      {/* ── Top header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 20px",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Space size={12} wrap>
          <CalendarOutlined style={{ fontSize: 20, color: "#1677ff" }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>
              {calendar.termName} {calendar.academicYear} — Co-Curricular
              Calendar
            </div>
            <Space size={8} wrap style={{ marginTop: 2 }}>
              <Tag color={STATUS_COLORS[calendar.status] || "default"}>
                {calendar.status}
              </Tag>
              <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                {activeEvents} events
              </span>
              {cancelledEvents > 0 && (
                <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                  {cancelledEvents} cancelled
                </span>
              )}
            </Space>
          </div>
        </Space>

        <Space wrap>
          {isAdmin && calendar.status === "Draft" && (
            <Popconfirm
              title="Submit this calendar for approval?"
              description="The calendar must have at least one event."
              onConfirm={() => onSubmit(calendar.id)}
              okText="Submit"
            >
              <Button type="primary" icon={<SendOutlined />}>
                Submit for Approval
              </Button>
            </Popconfirm>
          )}
          {isAdmin &&
            calendar.status !== "Draft" &&
            calendar.status !== "Published" && (
              <>
                <Popconfirm
                  title="Approve this calendar?"
                  onConfirm={() => onApprove(calendar.id)}
                  okText="Approve"
                >
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                    }}
                    icon={<CheckCircleOutlined />}
                  >
                    Approve
                  </Button>
                </Popconfirm>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => onReject(calendar)}
                >
                  Reject
                </Button>
              </>
            )}
          <Tooltip title="Refresh events">
            <Button
              icon={<SyncOutlined />}
              onClick={onRefresh}
              loading={loading}
            />
          </Tooltip>
          <Tooltip title="Print / Export PDF">
            <Button icon={<PrinterOutlined />} onClick={handlePrint} />
          </Tooltip>
          <Button onClick={onClose}>Close</Button>
        </Space>
      </div>

      {/* ── Calendar toolbar ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 20px",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Space>
          <Button size="small" onClick={goToToday}>
            Today
          </Button>
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={goPrev}
            size="small"
          />
          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={goNext}
            size="small"
          />
          <span style={{ fontSize: 17, fontWeight: 600, minWidth: 200 }}>
            {navLabel}
          </span>
        </Space>

        <Space>
          {canEdit && (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                onAddEvent(
                  view === "month"
                    ? currentDate
                    : currentDate.startOf("isoWeek"),
                )
              }
            >
              Add Event
            </Button>
          )}
          <div
            style={{
              display: "flex",
              border: "1px solid #d9d9d9",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            {["month", "week"].map((v) => (
              <div
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: "4px 14px",
                  fontSize: 13,
                  cursor: "pointer",
                  backgroundColor: view === v ? "#1677ff" : "#fff",
                  color: view === v ? "#fff" : "#595959",
                  fontWeight: view === v ? 600 : 400,
                  transition: "all 0.15s",
                  userSelect: "none",
                }}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </div>
            ))}
          </div>
        </Space>
      </div>

      {/* ── Calendar body ── */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          overscrollBehavior: "contain",
          padding: "12px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          position: "relative",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              zIndex: 10,
            }}
          >
            <Spin size="large" tip="Loading events..." />
          </div>
        )}

        {calendar.remarks && (
          <Alert
            message={`Rejection remarks: ${calendar.remarks}`}
            type="warning"
            showIcon
            closable
          />
        )}

        {/* Legend */}
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {Object.entries(CATEGORY_STYLES).map(([cat, s]) => (
            <div
              key={cat}
              style={{ display: "flex", alignItems: "center", gap: 5 }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  backgroundColor: s.bg,
                  border: `2px solid ${s.border}`,
                }}
              />
              <span style={{ fontSize: 11, color: "#595959" }}>{cat}</span>
            </div>
          ))}
          {canEdit && (
            <span style={{ fontSize: 11, color: "#bfbfbf", marginLeft: 4 }}>
              · Click any day to add an event
            </span>
          )}
        </div>

        {/* Calendar grid */}
        {view === "month" ? (
          <MonthView
            year={currentDate.year()}
            month={currentDate.month()}
            eventsMap={eventsMap}
            today={today}
            canEdit={canEdit}
            termStart={termStart}
            termEnd={termEnd}
            onDayClick={onAddEvent}
            onEditEvent={onEditEvent}
            onCancelEvent={onCancelEvent}
            onDeleteEvent={onDeleteEvent}
          />
        ) : (
          <WeekView
            referenceDate={currentDate}
            eventsMap={eventsMap}
            today={today}
            canEdit={canEdit}
            termStart={termStart}
            termEnd={termEnd}
            onDayClick={onAddEvent}
            onEditEvent={onEditEvent}
            onCancelEvent={onCancelEvent}
            onDeleteEvent={onDeleteEvent}
          />
        )}
      </div>
    </Modal>
  );
};

export default CurricularCalendarView;
